import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getProjectStaffing, getStaffingMonthly, listScenarios, setStaffingWeeks } from '../../api/client'
import { currentLocale } from '../../i18n'
import { useAuth } from '../../context/AuthContext'
import { HoursCell } from '../staffing/HoursCell'
import { isoWeekNumber, weekTouchesMonth } from '../staffing/weeks'
import VisibilityMenu from './VisibilityMenu'
import { num, projectAlias, projectCode, projectTitle } from './sheet'

/**
 * "Disponibilidad -- por mes": the same committed dotacion as "por proyecto"
 * and "por semana", read by calendar month instead of by week, and split
 * into a discipline read off each person's own Cargo (see
 * staffing_matrix.discipline_of on the backend -- Cargo is a fixed dropdown,
 * so this is an exact lookup, not a guess).
 *
 * A project's row expands into its actual roster, editable week by week --
 * the same cell component and the same endpoint the project's own Dotacion
 * tab uses, so a number typed here and a number typed there are the same
 * write. The expanded roster is filtered to whichever disciplines are
 * currently selected, the same way the row's own cells already are -- each
 * `detail` row carries its own `discipline` for exactly that.
 */
const WINDOWS = [3, 6, 12]
// The "Sin asignar" pseudo-row's id -- matches staffing_matrix.UNUSED_KEY,
// the "por proyecto" view's equivalent column, so the two never drift apart.
const UNUSED_KEY = 'unused'
const DISCIPLINES = ['mec', 'elect', 'software', 'factory', 'otros']
const DISCIPLINE_KEY = {
  mec: 'disciplineMec', elect: 'disciplineElect', software: 'disciplineSoftware',
  factory: 'disciplineFactory', otros: 'disciplineOtros',
}
// A cell's own filtered buckets/total, and which of those buckets were
// capped -- shared by the row, the footer, and the project-visibility
// check, so the three can never disagree on what "showing" means once a
// discipline filter is active.
function cellFor(monthData, activeDisciplines) {
  if (!monthData) return { buckets: {}, total: 0, trimmed: new Set() }
  const buckets = {}
  const trimmed = new Set()
  let total = 0
  for (const d of DISCIPLINES) {
    if (!activeDisciplines.has(d) || !monthData.buckets[d]) continue
    buckets[d] = monthData.buckets[d]
    total += monthData.buckets[d]
    if (monthData.trimmed?.includes(d)) trimmed.add(d)
  }
  return { buckets, total: Math.round(total * 100) / 100, trimmed }
}

// Whole-company committed/available for whichever disciplines are active,
// for one month -- independent of which projects are hidden on screen (see
// build_staffing_monthly's own discipline_totals: hiding a project there is
// a display choice, not a claim its hours stopped happening).
function disciplineSummary(monthKey, disciplineTotals, activeDisciplines) {
  const entry = disciplineTotals?.[monthKey] || {}
  let committed = 0
  let available = 0
  for (const d of DISCIPLINES) {
    if (!activeDisciplines.has(d) || !entry[d]) continue
    committed += entry[d].committed
    available += entry[d].available
  }
  committed = Math.round(committed * 100) / 100
  available = Math.round(available * 100) / 100
  const pct = available ? Math.round((100 * committed / available) * 10) / 10 : null
  return { committed, available, pct }
}

const monthLabel = (year, month) => new Date(year, month - 1, 1)
  .toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' })
const weekLabel = (iso) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`
// This week's own Monday, as an ISO string -- same rounding as the backend's
// gantt_calendar.monday_of, so a scenario departure mid-week still blocks the
// WHOLE week it falls in rather than trying to split one.
const mondayOfIso = (iso) => {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function StaffingMonthly() {
  const { t } = useTranslation('clevel')
  const { user } = useAuth()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [count, setCount] = useState(WINDOWS[0])
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [fullscreen, setFullscreen] = useState(false)
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set())
  const [activeDisciplines, setActiveDisciplines] = useState(() => new Set(DISCIPLINES))
  const [showDetail, setShowDetail] = useState(false)   // false = just the month's total, no per-discipline lines -- the default view
  const [expanded, setExpanded] = useState(() => new Set())
  const [details, setDetails] = useState({})        // project_id -> staffing payload
  const [detailErrors, setDetailErrors] = useState({})
  const [saving, setSaving] = useState(null)        // "projectId:rowId:monday"
  const [scenarioId, setScenarioId] = useState('')
  const [scenarios, setScenarios] = useState([])

  const k = useCallback((suffix, opts) => t(`capacity.byMonth.${suffix}`, opts), [t])

  const roles = user?.roles || (user?.role ? [user.role] : [])
  // c_level EXACTLY -- mirrors require_exactly("c_level") on scenario_id
  // (routers/capacity.py). While a scenario is selected, the per-project
  // edit below (setStaffingWeeks) writes THAT scenario's own phantom project
  // instead of the real one -- the whole point of cloning it. Accepted
  // trade-off: hand-editing a row a fire action already touched can drift
  // from that action's recorded moved_hours (scenario_staffing.py), so
  // removing the fire action later restores ITS numbers, not whatever was
  // hand-edited afterward -- "Update" always gives a clean slate.
  const canScenarios = roles.includes('c_level')
  const canEdit = scenarioId ? canScenarios : ['admin', 'pm_po'].some(r => roles.includes(r))

  useEffect(() => {
    if (!canScenarios) return
    listScenarios().then(setScenarios).catch(() => {})
  }, [canScenarios])

  // {team_member_id: fire_date} for the scenario currently selected, so a
  // fired person's own future cells can be locked -- editing them back would
  // silently un-fire them outside the fire-action mechanism (and its
  // moved_hours bookkeeping, see scenario_staffing.py) entirely.
  const fireDates = scenarioId
    ? Object.fromEntries((scenarios.find(s => String(s.id) === String(scenarioId))?.fire_actions || [])
        .map(a => [a.team_member_id, mondayOfIso(a.fire_date)]))
    : {}
  const isFired = (teamMemberId, monday) => {
    const fireMonday = fireDates[teamMemberId]
    return !!fireMonday && monday >= fireMonday
  }

  const load = useCallback(() => {
    getStaffingMonthly({ year, month, count, scenarioId: canScenarios ? scenarioId : '' })
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [year, month, count, scenarioId, canScenarios, k])

  useEffect(() => { load() }, [load])
  // A project's expanded detail is the scenario's version of it: drop what was loaded for another view.
  useEffect(() => { setDetails({}) }, [scenarioId])

  useEffect(() => {
    if (!fullscreen) return undefined
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [fullscreen])

  const goWindow = (size) => setCount(size)
  const shift = (delta) => {
    let m = month + delta * count
    let y = year
    while (m < 1) { m += 12; y -= 1 }
    while (m > 12) { m -= 12; y += 1 }
    setYear(y)
    setMonth(m)
  }

  const toggleHiddenProject = (id) => setHiddenProjects(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  // Toggling a discipline on its own is enough to isolate it (click just
  // "Fab" from the full set and everything else drops out) -- but toggling
  // OFF the last one left would empty the table with no way back from the
  // buttons themselves, so that one click is read as "start over" instead.
  const toggleDiscipline = (d) => setActiveDisciplines(prev => {
    if (prev.size === DISCIPLINES.length) return new Set([d])
    const next = new Set(prev)
    next.has(d) ? next.delete(d) : next.add(d)
    return next.size ? next : new Set(DISCIPLINES)
  })

  const toggleExpand = (projectId) => {
    const opening = !expanded.has(projectId)
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(projectId) ? next.delete(projectId) : next.add(projectId)
      return next
    })
    // "Sin asignar" has no real project to fetch -- its roster is already
    // sitting in `data.unused.roster`, read-only, nothing to edit.
    if (opening && !details[projectId] && projectId !== UNUSED_KEY) {
      getProjectStaffing(projectId, canScenarios ? scenarioId : '')
        .then(detail => setDetails(prev => ({ ...prev, [projectId]: detail })))
        .catch(err => setDetailErrors(prev => ({
          ...prev, [projectId]: err.response?.data?.detail || k('loadDetailFailed'),
        })))
    }
  }

  const saveCell = async (projectId, rowId, monday, value) => {
    const key = `${projectId}:${rowId}:${monday}`
    setSaving(key)
    try {
      await setStaffingWeeks(projectId, rowId, { week_start: monday, hours: value }, canScenarios ? scenarioId : '')
      const [detail, monthly] = await Promise.all([
        getProjectStaffing(projectId, canScenarios ? scenarioId : ''),
        getStaffingMonthly({ year, month, count, scenarioId: canScenarios ? scenarioId : '' }),
      ])
      setDetails(prev => ({ ...prev, [projectId]: detail }))
      setData(monthly)
    } catch (err) {
      setError(err.response?.data?.detail || k('saveFailed'))
    } finally {
      setSaving(null)
    }
  }

  if (error && !data) {
    return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  }
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  const allProjects = data.projects
  // A project stays out once none of the disciplines still showing have any
  // hours anywhere in the visible months -- the same "nothing left to show"
  // rule VisibilityMenu's own hiding already follows, just driven by the
  // discipline filter instead of the project checklist.
  const filteredTotal = (p) => data.months.reduce(
    (s, m) => s + cellFor(p.by_month[m.key], activeDisciplines).total, 0)
  const visibleProjects = allProjects
    .filter(p => !hiddenProjects.has(p.project_id))
    .filter(p => activeDisciplines.size === DISCIPLINES.length || filteredTotal(p) > 0)

  // Rendered as one more row at the end of the table, but deliberately kept
  // out of `visibleProjects`/the footer TOTAL: it isn't a project anyone
  // committed hours to, so summing it into "how many hours are committed"
  // would be wrong. Still respects the discipline filter, the same way a
  // real project's cell does (it only ever has non-"otros" buckets to begin
  // with, so filtering down to just "Otros" correctly empties it).
  const unusedProject = data.unused && {
    project_id: UNUSED_KEY, name: null, code: null, nickname: null,
    by_month: data.unused.by_month, total: data.unused.total,
    roster: data.unused.roster, is_unused: true,
  }
  const unusedFilteredTotal = unusedProject ? filteredTotal(unusedProject) : 0
  const showUnusedRow = unusedProject
    && (activeDisciplines.size === DISCIPLINES.length || unusedFilteredTotal > 0)
  const bodyRows = showUnusedRow ? [...visibleProjects, unusedProject] : visibleProjects

  const label = data.months.length
    ? `${monthLabel(data.months[0].year, data.months[0].month)} – ${
      monthLabel(data.months[data.months.length - 1].year, data.months[data.months.length - 1].month)}`
    : ''

  const headCell = 'sticky top-0 z-20 bg-white border-b border-gray-200 px-2 py-2 text-center whitespace-nowrap font-medium text-gray-500'
  const nameCell = 'sticky left-0 z-10 px-3 py-1.5'

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-40 bg-gray-50 overflow-auto p-4 space-y-3'
      : 'space-y-4'}>
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {WINDOWS.map(size => (
            <button key={size} onClick={() => goWindow(size)}
              className={`px-3 py-1.5 ${count === size ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`window_${size}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 min-w-32 text-center">{label}</span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        <VisibilityMenu
          label={k('showProjects')}
          items={allProjects}
          hidden={hiddenProjects}
          getKey={p => p.project_id}
          getLabel={p => projectTitle(p)}
          getHint={p => num(p.total)}
          onToggle={toggleHiddenProject}
          onShowAll={() => setHiddenProjects(new Set())}
          onHideAll={() => setHiddenProjects(new Set(allProjects.map(p => p.project_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />

        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs"
             title={k('disciplineFilterHint')}>
          {DISCIPLINES.map(d => (
            <button key={d} onClick={() => toggleDiscipline(d)}
              className={`px-2.5 py-1.5 ${activeDisciplines.has(d)
                ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(DISCIPLINE_KEY[d])}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input type="checkbox" checked={!showDetail}
                 onChange={e => setShowDetail(!e.target.checked)} />
          {k('totalsOnly')}
        </label>

        {canScenarios && (
          <select
            value={scenarioId}
            onChange={e => setScenarioId(e.target.value)}
            className={`text-xs border rounded-lg px-2 py-1.5 bg-white ${
              scenarioId ? 'border-amber-300 text-amber-800' : 'border-gray-200 text-gray-700'}`}
          >
            <option value="">{k('realPlan')}</option>
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setFullscreen(v => !v)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {fullscreen ? `⤡ ${k('fullscreenExit')}` : `⤢ ${k('fullscreen')}`}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}
      {scenarioId ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {k('scenarioEditing')}
        </p>
      ) : !canEdit && (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          {k('readOnly')}
        </p>
      )}

      <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${
        fullscreen ? 'max-h-[calc(100vh-13rem)]' : 'max-h-[65vh]'}`}>
        <table className="w-full text-xs border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={`${headCell} sticky left-0 z-30 text-left font-semibold text-gray-600`}
                  style={{ minWidth: 220 }}>
                {k('project')}
              </th>
              {data.months.map(m => (
                <th key={m.key} className={`${headCell} font-medium text-gray-500`} style={{ minWidth: 96 }}>
                  {monthLabel(m.year, m.month)}
                </th>
              ))}
              <th className={`${headCell} font-semibold text-gray-600`}>{k('total')}</th>
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((p, i) => {
              const open = expanded.has(p.project_id)
              const stripe = p.is_unused ? 'bg-emerald-50/50' : i % 2 ? 'bg-gray-50/60' : 'bg-white'
              const detail = details[p.project_id]
              const detailError = detailErrors[p.project_id]
              const visibleMondays = detail
                ? detail.weeks.filter(w => data.months.some(m => weekTouchesMonth(w.monday, m.year, m.month)))
                : []
              return [
                <tr key={p.project_id} className={stripe}>
                  <td className={`${nameCell} bg-inherit ${p.is_unused ? 'text-emerald-800' : 'text-gray-800'}`}
                      title={p.is_unused ? k('unusedProject') : projectTitle(p)}>
                    <button onClick={() => toggleExpand(p.project_id)}
                            className="text-gray-400 hover:text-gray-900 mr-1.5"
                            title={k('expandHint')}>
                      {open ? '▾' : '▸'}
                    </button>
                    <span className="font-medium">
                      {p.is_unused ? k('unusedProject') : projectCode(p)}
                    </span>
                    {!p.is_unused && projectAlias(p) && <span className="text-gray-400 ml-1">{projectAlias(p)}</span>}
                  </td>
                  {data.months.map(m => {
                    const { buckets, total, trimmed } = cellFor(p.by_month[m.key], activeDisciplines)
                    const shown = DISCIPLINES.filter(d => buckets[d])
                    if (shown.length === 0) {
                      return (
                        <td key={m.key} className="px-2 py-1.5 align-top">
                          <span className="text-gray-300">·</span>
                        </td>
                      )
                    }
                    if (!showDetail) {
                      return (
                        <td key={m.key} className={`px-2 py-1.5 text-right tabular-nums align-top ${
                          trimmed.size > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}`}
                            title={trimmed.size > 0 ? k('trimmedHint') : undefined}>
                          {num(total)}
                        </td>
                      )
                    }
                    return (
                      <td key={m.key} className="px-2 py-1.5 align-top">
                        <div className="space-y-0.5">
                          {shown.map(d => (
                            <div key={d} className={`flex justify-between gap-2 tabular-nums ${
                              trimmed.has(d) ? 'text-red-600 font-semibold' : 'text-gray-700'}`}
                              title={trimmed.has(d) ? k('trimmedHint') : k('disciplineHint')}>
                              <span className={trimmed.has(d) ? 'text-red-500' : 'text-gray-400'}>
                                {k(DISCIPLINE_KEY[d])}
                              </span>
                              <span>{num(buckets[d])}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    )
                  })}
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium text-gray-900">
                    {activeDisciplines.size === DISCIPLINES.length ? num(p.total) : num(filteredTotal(p))}
                  </td>
                </tr>,
                ...(open && p.is_unused ? [
                  <tr key={`${p.project_id}-detail`}>
                    <td colSpan={data.months.length + 2} className="p-0 border-b border-gray-200">
                      <table className="w-full text-xs border-separate border-spacing-0 bg-emerald-50/30">
                        <thead>
                          <tr>
                            <th className="sticky left-0 z-10 bg-emerald-50/50 text-left px-3 py-1.5 font-medium text-gray-500"
                                style={{ minWidth: 220 }}>
                              {k('disciplineHeader')}
                            </th>
                            {data.months.map(m => (
                              <th key={m.key} className="px-2 py-1.5 text-center font-medium text-gray-500"
                                  style={{ minWidth: 96 }}>
                                {monthLabel(m.year, m.month)}
                              </th>
                            ))}
                            <th className="px-2 py-1.5 text-right font-medium text-gray-500">{k('total')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Same rule the row's own cells already follow: with a
                            // discipline filter active, the roster shows only the
                            // matching people -- picking "Ing M." and expanding
                            // "Sin asignar" means only mechanical engineers show up.
                            const visibleRoster = p.roster.filter(person => activeDisciplines.has(person.discipline))
                            return visibleRoster.length === 0 ? (
                              <tr>
                                <td colSpan={data.months.length + 2} className="px-4 py-3 text-center text-gray-400">
                                  {k('noStaffing')}
                                </td>
                              </tr>
                            ) : visibleRoster.map(person => (
                              <tr key={person.team_member_id}>
                                <td className="sticky left-0 z-10 bg-emerald-50/50 px-3 py-1 text-gray-700 truncate"
                                    title={`${person.name} — ${k(DISCIPLINE_KEY[person.discipline])}`}>
                                  {person.name}
                                  <span className="text-gray-400 ml-1">{k(DISCIPLINE_KEY[person.discipline])}</span>
                                </td>
                                {data.months.map(m => (
                                  <td key={m.key} className="px-2 py-1 text-right tabular-nums text-gray-700">
                                    {num(person.by_month[m.key])}
                                  </td>
                                ))}
                                <td className="px-2 py-1 text-right tabular-nums font-medium text-gray-900">
                                  {num(person.total)}
                                </td>
                              </tr>
                            ))
                          })()}
                        </tbody>
                      </table>
                    </td>
                  </tr>,
                ] : []),
                ...(open && !p.is_unused ? [
                  <tr key={`${p.project_id}-detail`}>
                    <td colSpan={data.months.length + 2} className="p-0 border-b border-gray-200">
                      {!detail && !detailError && (
                        <p className="text-xs text-gray-400 px-4 py-3">{k('loadingDetail')}</p>
                      )}
                      {detailError && (
                        <p className="text-xs text-red-600 px-4 py-3">{detailError}</p>
                      )}
                      {detail && (() => {
                        // Same rule the row's own cells already follow: with
                        // a discipline filter active, the roster underneath
                        // it shows only the matching people, never everyone
                        // on the project.
                        const visibleRows = detail.rows.filter(r => activeDisciplines.has(r.discipline))
                        return (
                        <table className="w-full text-xs border-separate border-spacing-0 bg-blue-50/20">
                          <thead>
                            <tr>
                              <th className="sticky left-0 z-10 bg-blue-50/40 text-left px-3 py-1.5 font-medium text-gray-500"
                                  style={{ minWidth: 220 }} />
                              {visibleMondays.map(w => (
                                <th key={w.monday} className="px-1 py-1.5 text-center font-medium text-gray-500"
                                    style={{ minWidth: 56 }}>
                                  <div>S{isoWeekNumber(w.monday)}</div>
                                  <div className="text-[10px] text-gray-400">{weekLabel(w.monday)}</div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.map(row => (
                              <tr key={row.id}>
                                <td className="sticky left-0 z-10 bg-blue-50/40 px-3 py-1 text-gray-700 truncate"
                                    title={`${row.name} — ${k(DISCIPLINE_KEY[row.discipline])}`}>
                                  {row.name}
                                </td>
                                {visibleMondays.map(w => {
                                  const cell = row.weeks.find(c => c.monday === w.monday)
                                  const saveKey = `${p.project_id}:${row.id}:${w.monday}`
                                  return (
                                    <td key={w.monday} className="px-0.5 py-1 text-center"
                                        title={isFired(row.team_member_id, w.monday) ? k('firedCellHint') : undefined}>
                                      <HoursCell
                                        value={cell?.hours || 0}
                                        max={cell?.max_hours ?? row.weekly_hours}
                                        disabled={!canEdit || isFired(row.team_member_id, w.monday) || saving === saveKey}
                                        locked={isFired(row.team_member_id, w.monday)}
                                        onCommit={v => saveCell(p.project_id, row.id, w.monday, v)}
                                      />
                                    </td>
                                  )
                                })}
                              </tr>
                            ))}
                            {visibleRows.length === 0 && (
                              <tr>
                                <td colSpan={visibleMondays.length + 1}
                                    className="px-4 py-3 text-center text-gray-400">
                                  {k('noStaffing')}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        )
                      })()}
                    </td>
                  </tr>,
                ] : []),
              ]
            })}
            {bodyRows.length === 0 && (
              <tr>
                <td colSpan={data.months.length + 2} className="px-4 py-8 text-center text-gray-400">
                  {k('noStaffing')}
                </td>
              </tr>
            )}
          </tbody>
          {bodyRows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-gray-900 border-t border-gray-300">
                  {k('totalRow')}
                </td>
                {data.months.map(m => (
                  <td key={m.key} className="px-2 py-2 text-right tabular-nums border-t border-gray-300 text-gray-800">
                    {num(visibleProjects.reduce(
                      (s, p) => s + cellFor(p.by_month[m.key], activeDisciplines).total, 0))}
                  </td>
                ))}
                <td className="px-2 py-2 text-right tabular-nums text-gray-900 border-t border-gray-300">
                  {num(visibleProjects.reduce((s, p) => s + (activeDisciplines.size === DISCIPLINES.length
                    ? p.total : filteredTotal(p)), 0))}
                </td>
              </tr>
              <tr className="bg-gray-50 text-gray-500">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5" title={k('availableHint')}>
                  {k('availableRow')}
                </td>
                {data.months.map(m => (
                  <td key={m.key} className="px-2 py-1.5 text-right tabular-nums">
                    {num(disciplineSummary(m.key, data.discipline_totals, activeDisciplines).available)}
                  </td>
                ))}
                <td />
              </tr>
              <tr className="bg-gray-50 text-gray-500">
                <td className="sticky left-0 z-10 bg-gray-50 px-3 py-1.5" title={k('utilizationHint')}>
                  {k('utilizationRow')}
                </td>
                {data.months.map(m => {
                  const { pct } = disciplineSummary(m.key, data.discipline_totals, activeDisciplines)
                  return (
                    <td key={m.key} className={`px-2 py-1.5 text-right tabular-nums ${
                      pct == null ? '' : pct > 100 ? 'text-red-600 font-semibold'
                        : pct >= 85 ? 'text-amber-600 font-medium' : ''}`}>
                      {pct == null ? '—' : `${num(pct, 0)}%`}
                    </td>
                  )
                })}
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-[11px] text-gray-400">{k('legend')}</p>
    </div>
  )
}
